/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2026 Prodeo - All Rights Reserved
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dayjs, FORMAT_DATE_API, RootState, UUID } from '@umanforge/common';
import { useDispatch, useSelector } from 'react-redux';
import DataGrid, { Column, type CellSelectArgs, type RenderEditCellProps, type RowsChangeData } from 'react-data-grid';

import Button from '@common_components/buttons/button.component';
import DatePickerSoloInput from '@common_components/inputs/datePickerInputSolo.component';
import { ClickableIcon } from 'components/buttons/clickableIcon';
import { ArrowShortLeftIcon } from '@umanforge/common';
import { CrossIcon } from '@common_components/icons/cross.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';
import { SaveIcon } from '@common_components/icons/save.icon';

import { ChartValueService } from 'services/chartValue.service';
import { ToastService } from 'services/toast.service';

const toastService = new ToastService( 'ChartValueGrid' );

type GridRow = {
  id?: UUID;
  date: string;
  value: string;
  comment: string;
};

const parseDisplayDateValue = ( value: string ): Date | null => {
  const trimmed = value.trim();
  if ( !trimmed ) { return null; }

  const fmt = getDisplayFormat();
  const parsed = parseDateFromFormat( trimmed, fmt );
  if ( parsed ) { return parsed.toDate(); }

  if ( /^\d{4}-\d{1,2}-\d{1,2}$/.test( trimmed ) ) {
    const d = dayjs( trimmed );
    return d.isValid() ? d.toDate() : null;
  }

  return null;
};

const InlineCellEditor = ( { row, column, onRowChange, onClose }: RenderEditCellProps<GridRow> ) => {
  const cellValue = ( row as Record<string, string> )[ column.key ] ?? '';

  return (
    <div
      style={ {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'stretch',
        background: 'var(--rdg-background-color)',
        width: '100%'
      } }
    >
      <input
        autoFocus
        value={ cellValue }
        style={ {
          display: 'block',
          flex: 1,
          width: '100%',
          height: '100%',
          border: '2px solid #0ea5e9',
          outline: 'none',
          padding: '0 8px',
          margin: 0,
          background: 'transparent',
          color: 'inherit',
          font: 'inherit',
          boxSizing: 'border-box',
        } }
        onFocus={ e => { e.target.select(); } }
        onChange={ e => { onRowChange( { ...row, [ column.key ]: e.target.value } ); } }
        onBlur={ () => { onClose( true ); } }
        onKeyDown={ e => {
          if ( e.key === 'Enter' ) { onClose( true ); }
          if ( e.key === 'Escape' ) { e.stopPropagation(); onClose( false ); }
        } }
      />
    </div>
  );
};

const EDITABLE_COL_KEYS: Array<keyof Omit<GridRow, 'id'>> = [ 'date', 'value', 'comment' ];

const MAX_VALUE = 999_999_999;
const MAX_COMMENT_LENGTH = 200;

const clampValue = ( raw: string ): string => {
  const num = Number( raw );
  if ( !isNaN( num ) && num > MAX_VALUE ) { return String( MAX_VALUE ); }
  return raw;
};

const applyPasteRowCells = ( base: GridRow, pasteRow: string[], startColIdx: number ): GridRow => {
  const newRow = { ...base };
  pasteRow.forEach( ( cell, ci ) => {
    const colKey = EDITABLE_COL_KEYS[ startColIdx + ci ];
    if ( !colKey ) { return; }
    if ( colKey === 'date' ) {
      newRow.date = normalizeDisplayDate( cell.trim() );
    } else if ( colKey === 'value' ) {
      newRow.value = clampValue( cell.trim() );
    } else if ( colKey === 'comment' ) {
      newRow.comment = cell.trim().slice( 0, MAX_COMMENT_LENGTH );
    } else {
      ( newRow as Record<string, string> )[ colKey ] = cell.trim();
    }
  } );
  return newRow;
};

/* ===== Date helpers ===== */

/** Returns the locale short date format from dayjs (e.g. "DD/MM/YYYY", "MM/DD/YYYY", "DD.MM.YYYY"). */
const getDisplayFormat = (): string => {
  return ( dayjs.Ls[ dayjs.locale() ] as { formats?: { L?: string } } )?.formats?.L ?? 'DD/MM/YYYY';
};

/**
 * Parses a date string according to a dayjs format token string (e.g. "DD/MM/YYYY", "DD.MM.YYYY").
 * Returns a valid dayjs instance or null.
 */
const parseDateFromFormat = ( str: string, fmt: string ): dayjs.Dayjs | null => {
  // Strip trailing non-letter chars (e.g. Hungarian "YYYY.MM.DD.")
  const normalizedFmt = fmt.replace( /\W+$/u, '' );
  const sep = [ '/', '.', '-' ].find( s => { return normalizedFmt.includes( s ); } );
  if ( !sep ) { return null; }
  const fmtParts = normalizedFmt.split( sep );
  const strParts = str.trim().split( sep );
  if ( fmtParts.length !== 3 || strParts.length !== 3 ) { return null; }
  const tokenMap: Record<string, string> = {};
  fmtParts.forEach( ( token, i ) => { tokenMap[ token ] = strParts[ i ]; } );
  const year = tokenMap[ 'YYYY' ] ?? tokenMap[ 'YY' ];
  const month = tokenMap[ 'MM' ] ?? tokenMap[ 'M' ];
  const day = tokenMap[ 'DD' ] ?? tokenMap[ 'D' ];
  if ( !year || !month || !day ) { return null; }
  const iso = `${ year.padStart( 4, '0' ) }-${ month.padStart( 2, '0' ) }-${ day.padStart( 2, '0' ) }`;
  const d = dayjs( iso );
  return d.isValid() ? d : null;
};

const normalizeDisplayDate = ( raw: string ): string => {
  const trimmed = raw.trim();
  const fmt = getDisplayFormat();
  const parsed = parseDateFromFormat( trimmed, fmt );
  if ( parsed ) { return parsed.format( fmt ); }
  if ( /^\d{4}-\d{1,2}-\d{1,2}$/.test( trimmed ) ) {
    const d = dayjs( trimmed );
    return d.isValid() ? d.format( fmt ) : trimmed;
  }
  return trimmed;
};

const toDisplayDate = ( apiDate: string ): string => {
  const d = dayjs( apiDate, FORMAT_DATE_API );
  return d.isValid() ? d.format( getDisplayFormat() ) : apiDate;
};

const toApiDate = ( displayDate: string ): string | null => {
  const trimmed = displayDate.trim();
  const fmt = getDisplayFormat();
  const parsed = parseDateFromFormat( trimmed, fmt );
  if ( parsed ) { return parsed.format( FORMAT_DATE_API ); }
  if ( /^\d{4}-\d{1,2}-\d{1,2}$/.test( trimmed ) ) {
    const d = dayjs( trimmed );
    return d.isValid() ? d.format( FORMAT_DATE_API ) : null;
  }
  return null;
};

type ChartValueGridProps = {
  chartInfoId: UUID;
  onRefresh?: () => void;
  lastDate?: string | null;
  readStart?: string;
};

const ChartValueGrid: React.FC<ChartValueGridProps> = ( { chartInfoId, onRefresh, readStart } ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const [ rows, setRows ] = useState<GridRow[]>( [] );
  const [ markedForDeletion, setMarkedForDeletion ] = useState<Set<UUID>>( new Set() );
  const [ errorDate, setErrorDate ] = useState<string | null>( null );
  const [ isSaving, setIsSaving ] = useState( false );
  const [ isLoading, setIsLoading ] = useState( false );

  const originalRowsRef = useRef<GridRow[]>( [] );
  const selectedCellRef = useRef<{ rowIdx: number; colIdx: number } | null>( null );
  const gridContainerRef = useRef<HTMLDivElement>( null );

  const loadValues = useCallback( async () => {
    setIsLoading( true );
    const values = await ChartValueService.getAllValues( chartInfoId, token, dispatch );
    const mapped = values.map( v => {
      return {
        id: v.id,
        date: toDisplayDate( v.date ),
        value: String( v.value ),
        comment: v.comment ?? '',
      };
    } );
    originalRowsRef.current = mapped;
    setRows( mapped );
    setIsLoading( false );
  }, [ chartInfoId, dispatch ] );

  const getDefaultDisplayDate = useCallback( () => {
    try {
      const useYesterday = readStart === 'd-1';
      const d = useYesterday ? dayjs().subtract( 1, 'day' ) : dayjs();
      return d.format( getDisplayFormat() );
    } catch {
      return dayjs().format( getDisplayFormat() );
    }
  }, [ readStart ] );


  /* Paste multi-lignes depuis Excel (TSV) ── on intercepte Ctrl+V au keydown,
     AVANT que react-data-grid n'ouvre l'éditeur de cellule */
  useEffect( () => {
    const container = gridContainerRef.current;

    const handleGridPaste = ( text: string ) => {
      if ( !text ) { return; }

      const pasteRows = text
        .replace( /\r\n/g, '\n' )
        .replace( /\r/g, '\n' )
        .replace( /\n+$/g, '' )
        .split( '\n' )
        .filter( line => { return line.trim() !== ''; } )
        .map( line => { return line.split( '\t' ); } );

      if ( pasteRows.length === 0 ) { return; }

      const startRowIdx = selectedCellRef.current?.rowIdx ?? 0;
      const startColIdx = Math.min( selectedCellRef.current?.colIdx ?? 0, EDITABLE_COL_KEYS.length - 1 );

      setRows( prev => {
        const updated = [ ...prev ];
        const startRow = updated[ startRowIdx ];
        const isOnNewRow = !startRow || !startRow.id;

        if ( isOnNewRow ) {
          /* ── Mode INSERT : ligne nouvelle (verte) ── */
          const newRows: GridRow[] = pasteRows.map( pasteRow => {
            return applyPasteRowCells(
              { id: undefined, date: getDefaultDisplayDate(), value: '', comment: '' },
              pasteRow,
              startColIdx
            );
          } );
          const isEmptyPlaceholder = startRow && !startRow.id && !startRow.value.trim() && !startRow.comment.trim();
          updated.splice( startRowIdx, isEmptyPlaceholder ? 1 : 0, ...newRows );
        } else {
          /* ── Mode OVERWRITE : ligne sauvegardée ── */
          pasteRows.forEach( ( pasteRow, ri ) => {
            const rowIndex = startRowIdx + ri;
            const base: GridRow = updated[ rowIndex ] ?? {
              id: undefined,
              date: getDefaultDisplayDate(),
              value: '',
              comment: '',
            };
            const newRow = applyPasteRowCells( base, pasteRow, startColIdx );
            if ( rowIndex < updated.length ) {
              updated[ rowIndex ] = newRow;
            } else {
              updated.push( newRow );
            }
          } );
        }

        return updated;
      } );
    };

    const onKeyDown = ( e: KeyboardEvent ) => {
      const isCtrlV = ( e.ctrlKey || e.metaKey ) && e.key === 'v';
      if ( !isCtrlV ) { return; }

      const target = e.target as HTMLElement;
      if ( !container?.contains( target ) ) { return; }

      /* Si l'utilisateur est en mode édition (double-clic), laisser le collé natif */
      if ( target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable ) { return; }

      /* On est en mode sélection de cellule : on empêche react-data-grid d'ouvrir l'éditeur */
      e.preventDefault();
      e.stopPropagation();

      /* Lecture du presse-papier via Clipboard API (async, user-gesture garanti ici) */
      navigator.clipboard.readText()
        .then( handleGridPaste )
        .catch( () => { /* Permission refusée ou API indisponible */ } );
    };

    container?.addEventListener( 'keydown', onKeyDown, { capture: true } );
    return () => { container?.removeEventListener( 'keydown', onKeyDown, { capture: true } ); };
  }, [] );

  useEffect( () => {
    checkDates();
  }, [ rows, markedForDeletion ] );

  useEffect( () => {
    loadValues();
  }, [ loadValues ] );

  const toggleMarkForDeletion = useCallback( ( rowIdx: number ) => {
    setMarkedForDeletion( prev => {
      const next = new Set( prev );
      const row = rows[ rowIdx ];
      if ( row && row.id ) {
        if ( next.has( row.id ) ) {
          next.delete( row.id );
        } else {
          next.add( row.id );
        }
      } else {
        setRows( rows.filter( item => { return item != row; } ) );
      }
      return next;
    } );
  }, [ rows ] );

  /* Lignes modifiées (existantes dont les données diffèrent de l'original) */
  const modifiedRows = useMemo<Set<number>>( () => {
    const modified = new Set<number>();
    rows.forEach( ( row, idx ) => {
      if ( !row.id ) { return; }
      const original = originalRowsRef.current.find( r => { return r.id === row.id; } );
      if ( !original ) { return; }
      if ( row.date !== original.date || row.value !== original.value || row.comment !== original.comment ) {
        modified.add( idx );
      }
    } );
    return modified;
  }, [ rows ] );

  const revertRow = useCallback( ( rowIdx: number ) => {
    setRows( prev => {
      const row = prev[ rowIdx ];
      if ( !row?.id ) { return prev; }
      const original = originalRowsRef.current.find( r => { return r.id === row.id; } );
      if ( !original ) { return prev; }
      const updated = [ ...prev ];
      updated[ rowIdx ] = { ...original };
      return updated;
    } );
  }, [] );

  /* Normalise la date, clampe la valeur et tronque le commentaire à la saisie */
  const handleRowsChange = useCallback( ( updatedRows: GridRow[], data: RowsChangeData<GridRow> ) => {
    const processed = [ ...updatedRows ];
    data.indexes.forEach( idx => {
      const row = processed[ idx ];
      if ( data.column.key === 'date' ) {
        processed[ idx ] = { ...row, date: normalizeDisplayDate( row.date ) };
      } else if ( data.column.key === 'value' ) {
        processed[ idx ] = { ...row, value: clampValue( row.value ) };
      } else if ( data.column.key === 'comment' && row.comment.length > MAX_COMMENT_LENGTH ) {
        processed[ idx ] = { ...row, comment: row.comment.slice( 0, MAX_COMMENT_LENGTH ) };
      }
    } );
    setRows( processed );
  }, [] );

  const columns = useMemo<Column<GridRow>[]>( () => {
    return [
      {
        key: 'date',
        name: t( 'translation:chart_value_label_date' ),
        renderCell: ( { row, rowIdx } ) => {
          const isLocked = markedForDeletion.has( row.id ?? '' );
          return (
            <div className="h-full w-full flex items-center rounded-md overflow-hidden">
              <DatePickerSoloInput
                defaultValue={ parseDisplayDateValue( row.date ) }
                maxDate={ dayjs().endOf( 'day' ).toDate() }
                disable={ isLocked }
                compact
                onChange={ date => {
                  const nextValue = date ? dayjs( date ).format( getDisplayFormat() ) : '';
                  setRows( prev => {
                    return prev.map( ( item, index ) => {
                      return index === rowIdx ? { ...item, date: nextValue } : item;
                    } );
                  } );
                } }
                width="w-full"
                shadow="shadow-none"
                border="border-0"
                inputClassName="w-full h-full min-h-0 px-2 py-0 text-sm bg-transparent"
              />
            </div>
          );
        },
        editable: false,
        width: 180,
      },
      {
        key: 'value',
        name: t( 'translation:chart_value_label_value' ),
        renderEditCell: InlineCellEditor,
        editable: row => { return !markedForDeletion.has( row.id ?? '' ); },
        width: 100,
      },
      {
        key: 'comment',
        name: t( 'translation:chart_value_label_comment' ),
        renderEditCell: InlineCellEditor,
        editable: row => { return !markedForDeletion.has( row.id ?? '' ); },
      },
      {
        key: 'actions',
        name: '',
        width: 42,
        cellClass: '!outline-none !shadow-none',
        renderCell: ( { row, rowIdx } ) => {
          const isMarked = markedForDeletion.has( row.id ?? '' );
          const isModified = modifiedRows.has( rowIdx );
          const isNew = !row.id;
          if ( isNew ) {
            return (
              <ClickableIcon
                icon={ <CrossIcon size={ 4 } className="text-green-600" /> }
                title={ t( 'common:delete' ) }
                onClick={ () => { setRows( prev => { return prev.filter( ( _, i ) => { return i !== rowIdx; } ); } ); } }
              />
            );
          }
          if ( isMarked ) {
            return (
              <ClickableIcon
                icon={ <ArrowShortLeftIcon className="text-red-500 rotate-180 w-4 h-4" /> }
                title={ t( 'common:cancel' ) }
                onClick={ () => { toggleMarkForDeletion( rowIdx ); } }
              />
            );
          }
          if ( isModified ) {
            return (
              <ClickableIcon
                icon={ <CrossIcon size={ 4 } className="text-blue-500" /> }
                title={ t( 'common:cancel' ) }
                onClick={ () => { revertRow( rowIdx ); } }
              />
            );
          }
          return (
            <ClickableIcon
              icon={ <CrossIcon size={ 4 } /> }
              title={ t( 'common:delete' ) }
              onClick={ () => { toggleMarkForDeletion( rowIdx ); } }
            />
          );
        },
      },
    ];
  }, [ t, toggleMarkForDeletion, markedForDeletion, modifiedRows, revertRow ] );

  const checkDates = () => {
    const currentRows = rows.filter( row => { return ![ ...markedForDeletion ].includes( row.id ?? '' ); } );
    const dates = currentRows
      .map( row => {
        const formattedDate = toApiDate( row.date );
        return formattedDate ? dayjs( formattedDate ) : null;
      } )
      .filter( date => { return date != null; } );

    console.log( dates.map( d => { return d.format( 'L' ); } ) );

    if ( dates.length != currentRows.length ) {
      setErrorDate( 'chart_value_invalid_date' );
    } else if ( dates.some( ( date, index, self ) => { return index !== self.findIndex( checkDate => { return checkDate.isSame( date, 'day' ); } ); } ) ) {
      setErrorDate( 'chart_value_duplicated_date' );
    } else {
      setErrorDate( null );
    }

  };

  const handleSave = async () => {
    if ( !errorDate ) {
      setIsSaving( true );
      let hasError = false;
      let hasInvalidDate = false;
      try {
        /* 1. Supprimer les lignes marquées */
        if ( markedForDeletion.size > 0 ) {
          const deleted = await ChartValueService.deleteValue( [ ...markedForDeletion ], token, dispatch );
          if ( !deleted ) { hasError = true; }

          if ( !hasError ) {
            setRows( prev => { return prev.filter( row => { return !markedForDeletion.has( row.id ?? '' ); } ); } );
            setMarkedForDeletion( new Set() );
          }
        }

        /* 2. Créer / mettre à jour les lignes restantes */
        const toCreate: { chartInfoId: UUID; value: number; date: string; comment: string }[] = [];
        const toUpdate: { id: UUID; value: number; comment: string }[] = [];
        const toDeleteAndCreate: { currentId: UUID, chartInfoId: UUID; value: number; date: string; comment: string }[] = [];

        const rowsToSave = rows.filter( row => { return !markedForDeletion.has( row.id ?? '' ); } );
        for ( const row of rowsToSave ) {
          const numericValue = Number( row.value );
          const apiDate = toApiDate( row.date );
          if ( !apiDate ) {
            if ( row.date.trim() !== '' ) { hasInvalidDate = true; }
            continue; // eslint-disable-line no-continue
          }
          if ( row.value === '' || isNaN( numericValue ) ) {
            continue; // eslint-disable-line no-continue
          }
          if ( row.id ) {
            const original = originalRowsRef.current.find( r => { return r.id === row.id; } );
            const dateChanged = !original || original.date !== row.date;
            const valueChanged = !original || Number( original.value ) !== numericValue;
            const commentChanged = !original || original.comment !== row.comment;

            if ( dateChanged && apiDate ) {
              toDeleteAndCreate.push( { currentId: row.id, chartInfoId, date: apiDate, value: numericValue, comment: row.comment } );
            } else {
              if ( valueChanged || commentChanged ) {
                toUpdate.push( { id: row.id, value: numericValue, comment: row.comment } );
              }
            }
          } else {
            toCreate.push( { chartInfoId, value: numericValue, date: apiDate, comment: row.comment } );
          }
        }
        if ( toDeleteAndCreate.length > 0 ) {
          const deleteChartValueIds = toDeleteAndCreate.map( value => { return value.currentId; } );
          const deleted = await ChartValueService.deleteValue( deleteChartValueIds, token, dispatch );
          if ( !deleted ) { hasError = true; }

          const postBody = toDeleteAndCreate.map( value => { return { chartInfoId: value.chartInfoId, value: value.value, date: value.date, comment: value.comment }; } );
          const created = await ChartValueService.postValue( postBody, token, dispatch );
          if ( !created ) { hasError = true; }
        }
        if ( toCreate.length > 0 ) {
          const created = await ChartValueService.postValue( toCreate, token, dispatch );
          if ( !created ) { hasError = true; }
        }
        if ( toUpdate.length > 0 ) {
          const updated = await ChartValueService.putValue( toUpdate, token, dispatch );
          if ( !updated ) { hasError = true; }
        }
      } finally {
        setIsSaving( false );
      }

      if ( hasInvalidDate ) {
        toastService.showError( 'translation:chart_value_invalid_date', { format: getDisplayFormat() } );
      }
      if ( !hasError ) {
        toastService.showSuccess( 'translation:chart_value_updated_success' );
        onRefresh?.();
        await loadValues();
      }
    }
  };

  return (
    <div className="flex flex-col gap-3">
      { isLoading && (
        <div className="text-sm text-gray-400">{ t( 'common:loading' ) }</div>
      ) }

      <div ref={ gridContainerRef } style={ { height: '400px', overflow: 'hidden', minWidth: 0, minHeight: 0 } }>
        <DataGrid
          columns={ columns }
          rows={ rows }
          onRowsChange={ handleRowsChange }
          className="rdg-light"
          style={ { height: '100%', minHeight: 0 } }
          rowHeight={ 35 }
          rowKeyGetter={ ( row: GridRow ) => { return row.id ?? row.date + '|' + row.value + '|' + rows.indexOf( row ); } }
          rowClass={ ( row, rowIdx ) => {
            if ( markedForDeletion.has( row.id ?? '' ) ) { return 'bg-red-50 line-through text-red-500'; }
            if ( modifiedRows.has( rowIdx ) ) { return 'bg-blue-50 text-blue-700'; }
            if ( !row.id ) { return 'bg-green-50 text-green-700'; }
            return undefined;
          } }
          onSelectedCellChange={ ( { rowIdx, column }: CellSelectArgs<GridRow> ) => {
            selectedCellRef.current = { rowIdx, colIdx: column.idx };
          } }
        />
      </div>

      <div className='flex mt-1 items-center'>
        <div className='w-full'>
          {
            errorDate && (
              <span className='text-red-base font-medium'>
                { t( errorDate, { format: getDisplayFormat() } ) }
              </span>
            )
          }
        </div>
        <div className="flex gap-2 justify-end">
          <Button
            label={ t( 'common:add' ) }
            icon={ <PlusIcon size={ 4 } className="group-hover:rotate-[360deg] transition-transform duration-500" /> }
            color="bg-gray-100"
            textColor="text-gray-600"
            colorHover="hover:bg-gray-200"
            textColorHover="hover:text-gray-700"
            hoverBorderColor="hover:border-transparent"
            shadow="shadow-none"
            padding="py-2 px-4"
            onClick={ () => {
              setRows( prev => {
                return [ { id: undefined, date: getDefaultDisplayDate(), value: '', comment: '' }, ...prev ];
              } );
            } }
          />
          <Button
            label={ isSaving ? t( 'common:saving' ) : t( 'common:save' ) }
            icon={ <SaveIcon size={ 4 } /> }
            color="bg-green-200"
            textColor="text-green-700"
            colorHover="hover:bg-green-300"
            textColorHover="hover:text-green-700"
            hoverBorderColor="hover:border-transparent"
            shadow="shadow-none"
            padding="py-2 px-4"
            disable={ isSaving || errorDate != null }
            onClick={ () => { void handleSave(); } }
          />
        </div>
      </div>



    </div>
  );
};

export default ChartValueGrid;
